import { type JSX, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Code,
  Divider,
  Drawer,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  type CampoDaConfiguracao,
  type EstadoDaConfiguracao,
  type LinhaDaConfiguracao,
  type ResultadoDaConfiguracao,
  CAMPOS_DA_CONFIGURACAO,
  ErroApi,
  api,
  explicarEnvio,
  problemasPorCampo,
} from '../lib/api.js';

/**
 * Configuração do disparo — painel lateral, só para admin.
 *
 * O QUE ESTA TELA RESOLVE. Teto diário, pausa, janela e prazo do adiado são
 * números que mudam com o uso, e ajustá-los exigia acesso ao Easypanel, editar
 * a variável em DOIS serviços e reiniciar os dois — com o agravante de que o
 * reinício no meio de um disparo deixa destinatário preso.
 *
 * TRÊS COISAS QUE A TELA TEM DE DIZER, e que moldam o layout:
 *
 *  1. DE ONDE VEM CADA VALOR. Sem o selo, ninguém sabe se o 200 foi escolhido
 *     por alguém ou é o default — e aí ninguém mexe, por medo de mudar o que
 *     não entende. O botão de limpar devolve o campo ao ambiente.
 *  2. A FAIXA PERMITIDA, antes de tentar. A validação do servidor explica a
 *     faixa quando alguém erra; mostrá-la no campo evita o erro.
 *  3. O ATRASO ATÉ O WORKER. A invalidação do cache acontece na api, e o
 *     worker é outro processo: ele relê em até 30s. Quem baixa o teto e vê o
 *     disparo seguir no ritmo antigo concluiria que a tela não funciona.
 *
 * A CHAVE GERAL NÃO É CAMPO DE FORMULÁRIO. Fica no topo, separada, com
 * confirmação ao LIGAR e efeito imediato ao DESLIGAR — ninguém deve ter de
 * salvar um formulário para conseguir parar o disparo.
 */

/**
 * Falha das rotas de configuração, explicada.
 *
 * UM 404 AQUI NÃO PODE SER TRADUZIDO PELA MENSAGEM DO SERVIDOR, e é o que
 * motivou esta função. Quando o painel está mais novo que a api, estas rotas
 * não existem lá e a requisição cai na paramétrica `/admin/envios/:id` — a api
 * antiga responde "envio não encontrado", e a tela repetia isso. A frase
 * mandava procurar um envio inexistente quando o problema era um deploy
 * desencontrado.
 *
 * A tela SABE o que pediu: um 404 numa rota de configuração só pode significar
 * que o servidor não a conhece. Então aqui o status vence o corpo.
 *
 * A api corrigida já devolve `id_invalido` com essa explicação — mas a
 * correção só vale depois de implantada, e é exatamente antes disso que a
 * mensagem precisa ajudar.
 */
function explicarFalhaDaConfiguracao(e: unknown, padrao: string): string {
  if (e instanceof ErroApi && e.status === 404) {
    return (
      'A api não conhece esta rota (404). Se o painel foi atualizado há pouco, ' +
      'a api pode estar numa versão anterior — confira se os dois serviços estão ' +
      'no mesmo deploy.'
    );
  }
  return explicarEnvio(e, padrao);
}

/** Campos numéricos, na ordem em que a tela os apresenta. */
const ORDEM: CampoDaConfiguracao[] = [
  'tetoDiario',
  'pausaMinSeg',
  'pausaMaxSeg',
  'janelaInicio',
  'janelaFim',
  'adiadoTetoHoras',
];

function SeloDeOrigem({ origem }: { origem: 'banco' | 'ambiente' }): JSX.Element {
  return origem === 'banco' ? (
    <Badge size="xs" variant="light" color="blue">
      definido no painel
    </Badge>
  ) : (
    <Tooltip label="Ninguém definiu no painel: vale o que está na variável de ambiente">
      <Badge size="xs" variant="light" color="gray">
        variável de ambiente
      </Badge>
    </Tooltip>
  );
}

export function ConfiguracaoEnvios({
  aberto,
  aoFechar,
  aoMudar,
}: {
  aberto: boolean;
  aoFechar: () => void;
  /** Avisa a seção de envios: a chave geral e os limites aparecem lá também. */
  aoMudar: () => void;
}): JSX.Element {
  const [estado, setEstado] = useState<EstadoDaConfiguracao | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [problemas, setProblemas] = useState<Record<string, string>>({});
  const [rascunho, setRascunho] = useState<Record<string, number | string | null>>({});
  const [salvando, setSalvando] = useState(false);
  const [confirmandoLigar, setConfirmandoLigar] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setEstado(await api.get<EstadoDaConfiguracao>('/admin/envios/configuracao'));
      setErro(null);
    } catch (e) {
      setErro(explicarFalhaDaConfiguracao(e, 'falha ao carregar a configuração'));
    }
  }, []);

  useEffect(() => {
    if (!aberto) return;
    setRascunho({});
    setProblemas({});
    void carregar();
  }, [aberto, carregar]);

  /** O que o campo mostra: o rascunho, se houver, senão o valor em vigor. */
  function valorNoCampo(linha: LinhaDaConfiguracao): number | string | null {
    const emRascunho = rascunho[linha.campo];
    return emRascunho === undefined ? linha.valor : emRascunho;
  }

  function mexido(linha: LinhaDaConfiguracao): boolean {
    return rascunho[linha.campo] !== undefined;
  }

  async function salvar(): Promise<void> {
    setSalvando(true);
    try {
      const r = await api.patch<ResultadoDaConfiguracao>(
        '/admin/envios/configuracao',
        rascunho,
      );
      setEstado(r.estado);
      setRascunho({});
      setProblemas({});
      notifications.show({
        message:
          r.alterados.length === 0
            ? 'Nada mudou.'
            : `${r.alterados.length} ${r.alterados.length === 1 ? 'campo' : 'campos'} salvos. O worker relê em até ${r.estado.atrasoDoWorkerSegundos}s.`,
        color: 'green',
      });
      aoMudar();
    } catch (e) {
      // Recusa de faixa vem por campo: marca o campo, não o rodapé.
      const porCampo = problemasPorCampo(e);
      setProblemas(porCampo);
      if (Object.keys(porCampo).length === 0) {
        setErro(explicarFalhaDaConfiguracao(e, 'falha ao salvar'));
      }
    } finally {
      setSalvando(false);
    }
  }

  async function trocarChave(ativo: boolean): Promise<void> {
    setSalvando(true);
    try {
      const r = await api.post<{ estado: EstadoDaConfiguracao }>(
        '/admin/envios/configuracao/ativo',
        { ativo },
      );
      setEstado(r.estado);
      notifications.show({
        title: ativo ? 'Disparo ligado' : 'Disparo desligado',
        message: ativo
          ? `Envios já podem ser iniciados. O worker enxerga em até ${r.estado.atrasoDoWorkerSegundos}s.`
          : 'Envios em curso vão pausar na próxima passagem, sem perder progresso.',
        color: ativo ? 'green' : 'orange',
      });
      aoMudar();
    } catch (e) {
      setErro(explicarFalhaDaConfiguracao(e, 'falha ao mudar a chave geral'));
    } finally {
      setSalvando(false);
      setConfirmandoLigar(false);
    }
  }

  const temRascunho = Object.keys(rascunho).length > 0;

  return (
    <Drawer
      opened={aberto}
      onClose={aoFechar}
      position="right"
      size="lg"
      title="Configuração do disparo"
    >
      {!estado && !erro && (
        <Group gap="xs">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Carregando…
          </Text>
        </Group>
      )}

      {erro && (
        <Alert color="red" variant="light" role="alert" mb="sm">
          <Group gap="sm">
            <Text size="sm">{erro}</Text>
            <Button size="xs" onClick={() => void carregar()}>
              Tentar de novo
            </Button>
          </Group>
        </Alert>
      )}

      {estado && (
        <Stack gap="md">
          {/* ----------------------------------------------------------------
              A chave geral. Fora do formulário, no topo, e com efeito próprio.
          ---------------------------------------------------------------- */}
          <Paper withBorder p="md" radius="md">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Stack gap={2}>
                <Group gap="xs">
                  <Text fw={600}>Disparo em massa</Text>
                  <SeloDeOrigem origem={estado.ativo.origem} />
                </Group>
                <Text size="xs" c="dimmed">
                  {estado.ativo.valor
                    ? 'Ligado: envios podem ser iniciados.'
                    : 'Desligado: a tela monta e revisa envios, mas iniciar é recusado.'}
                </Text>
                {estado.ativo.origem === 'ambiente' && (
                  <Text size="xs" c="dimmed">
                    Valendo o ambiente (<Code>ENVIO_ATIVO={String(estado.ativo.noAmbiente)}</Code>
                    ). Mexer aqui passa a sobrepor.
                  </Text>
                )}
              </Stack>

              <Switch
                size="md"
                checked={estado.ativo.valor}
                disabled={salvando}
                aria-label="Chave geral do disparo em massa"
                onChange={(e) => {
                  // LIGAR pede confirmação; DESLIGAR é imediato. Nunca o
                  // contrário: pedir confirmação para parar é o desenho que
                  // faz alguém não conseguir parar.
                  if (e.currentTarget.checked) setConfirmandoLigar(true);
                  else void trocarChave(false);
                }}
              />
            </Group>
          </Paper>

          <Alert color="blue" variant="light">
            <Text size="sm">
              As mudanças chegam ao worker em até{' '}
              <strong>{estado.atrasoDoWorkerSegundos} segundos</strong> — ele relê a configuração a
              cada passagem do disparo. Não é preciso reiniciar serviço nenhum.
            </Text>
          </Alert>

          <Divider label="Limites" labelPosition="left" />

          {ORDEM.map((campo) => {
            const linha = estado.campos.find((c) => c.campo === campo);
            if (!linha || linha.faixa === null) return null;
            const meta = CAMPOS_DA_CONFIGURACAO[campo];
            return (
              <Stack key={campo} gap={4}>
                <Group gap="xs" align="baseline">
                  <Text size="sm" fw={500}>
                    {meta.rotulo}
                  </Text>
                  <SeloDeOrigem origem={linha.origem} />
                  {mexido(linha) && (
                    <Badge size="xs" color="yellow" variant="light">
                      não salvo
                    </Badge>
                  )}
                </Group>

                <Group gap="xs" align="flex-start" wrap="nowrap">
                  <NumberInput
                    w={160}
                    size="xs"
                    /**
                     * O rótulo visível é um `<Text>` acima (para caber o selo
                     * de origem ao lado), então ele não está associado ao
                     * campo. `aria-label` dá o nome programático — sem isto o
                     * leitor de tela anuncia só "1 a 500", e nenhum teste
                     * consegue achar o campo pelo nome.
                     */
                    aria-label={meta.rotulo}
                    min={linha.faixa.min}
                    max={linha.faixa.max}
                    value={Number(valorNoCampo(linha) ?? linha.noAmbiente)}
                    error={problemas[campo]}
                    description={`${linha.faixa.min} a ${linha.faixa.max} · ${meta.unidade}`}
                    onChange={(v) =>
                      setRascunho((atual) => ({ ...atual, [campo]: Number(v) || 0 }))
                    }
                  />
                  {/* Limpar manda `null` e devolve o campo ao ambiente. Só
                      aparece quando há o que limpar. */}
                  {linha.origem === 'banco' && (
                    <Tooltip
                      label={`Volta para a variável de ambiente (${String(linha.noAmbiente)})`}
                      multiline
                      w={220}
                    >
                      <Button
                        size="xs"
                        variant="subtle"
                        mt={2}
                        onClick={() => setRascunho((atual) => ({ ...atual, [campo]: null }))}
                      >
                        Usar o ambiente
                      </Button>
                    </Tooltip>
                  )}
                </Group>

                <Text size="xs" c="dimmed">
                  {meta.ajuda}
                </Text>
              </Stack>
            );
          })}

          <Divider label="Instância" labelPosition="left" />

          <InstanciaDoDisparo
            linha={estado.campos.find((c) => c.campo === 'instancia') ?? null}
            instanciaDoAtendimento={estado.instanciaDoAtendimento}
            rascunho={rascunho['instancia']}
            aoMudar={(valor) => setRascunho((atual) => ({ ...atual, instancia: valor }))}
          />

          <Group justify="space-between" align="center">
            <Text size="xs" c="dimmed">
              {estado.atualizadoEm
                ? `Última alteração em ${new Date(estado.atualizadoEm).toLocaleString('pt-BR')}${
                    estado.atualizadoPor ? ` por ${estado.atualizadoPor}` : ''
                  }.`
                : 'Nada definido no painel ainda: tudo valendo as variáveis de ambiente.'}
            </Text>
            <Group gap="xs">
              <Button
                size="xs"
                variant="default"
                disabled={!temRascunho || salvando}
                onClick={() => {
                  setRascunho({});
                  setProblemas({});
                }}
              >
                Descartar
              </Button>
              <Button size="xs" loading={salvando} disabled={!temRascunho} onClick={() => void salvar()}>
                Salvar
              </Button>
            </Group>
          </Group>
        </Stack>
      )}

      <Modal
        opened={confirmandoLigar}
        onClose={() => setConfirmandoLigar(false)}
        title="Ligar o disparo em massa?"
        centered
        zIndex={400}
      >
        <Stack gap="sm">
          <Text size="sm">
            A partir daqui, qualquer envio em rascunho pode ser{' '}
            <strong>iniciado de verdade</strong> — as mensagens chegam no WhatsApp dos
            destinatários.
          </Text>
          <Text size="sm" c="dimmed">
            O disparo nasce desligado porque, enquanto o número for o mesmo do bot de atendimento,
            um bloqueio do WhatsApp derruba o atendimento inteiro. Os freios (teto, janela, pausa)
            continuam valendo.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmandoLigar(false)}>
              Não ligar
            </Button>
            <Button loading={salvando} onClick={() => void trocarChave(true)}>
              Ligar
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Drawer>
  );
}

/**
 * A instância, à parte dos numéricos.
 *
 * É texto livre e sem faixa, e carrega dois avisos que nenhum outro campo tem:
 * qual é a padrão do bot (para "vazio" significar algo concreto) e que trocá-la
 * invalida o teste dos envios em rascunho — que é a consequência menos óbvia e
 * a que faz alguém perder tempo procurando por que o botão de iniciar travou.
 */
function InstanciaDoDisparo({
  linha,
  instanciaDoAtendimento,
  rascunho,
  aoMudar,
}: {
  linha: LinhaDaConfiguracao | null;
  instanciaDoAtendimento: string;
  rascunho: number | string | null | undefined;
  aoMudar: (valor: string | null) => void;
}): JSX.Element | null {
  if (!linha) return null;
  const meta = CAMPOS_DA_CONFIGURACAO.instancia;
  const valor = rascunho === undefined ? (linha.noBanco ?? '') : (rascunho ?? '');

  return (
    <Stack gap={4}>
      <Group gap="xs" align="baseline">
        <Text size="sm" fw={500}>
          {meta.rotulo}
        </Text>
        <SeloDeOrigem origem={linha.origem} />
        {rascunho !== undefined && (
          <Badge size="xs" color="yellow" variant="light">
            não salvo
          </Badge>
        )}
      </Group>

      <TextInput
        size="xs"
        aria-label={meta.rotulo}
        placeholder={`vazio = ${instanciaDoAtendimento} (a do atendimento)`}
        value={String(valor)}
        onChange={(e) => aoMudar(e.currentTarget.value || null)}
        description={`Em vigor: ${String(linha.valor)}`}
      />

      <Text size="xs" c="dimmed">
        {meta.ajuda}
      </Text>
      <Text size="xs" c="dimmed">
        A padrão do bot é <Code>{instanciaDoAtendimento}</Code>. Apontar o disparo para uma
        instância dedicada é a única mitigação de verdade do risco de bloqueio: ela tira o
        atendimento da linha de fogo da campanha.
      </Text>
      <Alert color="yellow" variant="light" p="xs">
        <Text size="xs">
          Trocar a instância <strong>invalida o envio de teste</strong> dos envios em rascunho: o
          teste confirma a combinação de número, texto e lista, e o número passou a ser outro.
        </Text>
      </Alert>
    </Stack>
  );
}
